import React, { useState } from 'react';

const ImageCard = ({ image, articleSlug, imageNumber }) => {
  const [copiedAlt, setCopiedAlt] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  const altTextEntry = image.altTexts && image.altTexts.length > 0 ? image.altTexts[0] : null;
  const isDryRun = image.s3_url === 'DRY_RUN_PLACEHOLDER';
  const isFailed = image.s3_url === 'IMAGE_GENERATION_FAILED';
  const isUnavailable = isDryRun || isFailed;

  const handleDownload = () => {
    if (!isUnavailable && image.s3_url) {
      const filename = `${articleSlug}-image-${imageNumber}.png`;
      const downloadUrl = `/api/tools/article/download?url=${encodeURIComponent(image.s3_url)}&filename=${encodeURIComponent(filename)}`;
      window.location.href = downloadUrl;
    }
  };

  const handleCopyAlt = () => {
    if (altTextEntry) {
      navigator.clipboard.writeText(altTextEntry.alt_text);
      setCopiedAlt(true);
      setTimeout(() => setCopiedAlt(false), 2000);
    }
  };

  const getCounterColor = (count, limit) => {
    const ratio = count / limit;
    if (ratio > 1) return '#c5221f';
    if (ratio >= 0.8) return '#b06000';
    return '#137333';
  };

  const iconButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    backgroundColor: '#f8f9fa',
    color: '#495057',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  };

  return (
    <div style={{
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      backgroundColor: '#fff',
      overflow: 'hidden'
    }}>
      {/* Image */}
      {!isUnavailable && image.s3_url ? (
        <img
          src={image.s3_url}
          alt={altTextEntry?.alt_text || `Generated image ${imageNumber}`}
          style={{ width: '100%', display: 'block' }}
        />
      ) : isFailed ? (
        // Honest "no image" state — deliberately NOT styled to resemble a generated image.
        // We tell the user plainly that nothing was produced. Future (v0.0.9): a Regenerate
        // action (with prompt editing / issue flagging) would live inside this card.
        <div style={{
          width: '100%',
          aspectRatio: '1200/630',
          backgroundColor: '#f8fafc',
          border: '1px dashed #cbd5e1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#64748b'
        }}>
          <div style={{ textAlign: 'center', padding: '1rem', maxWidth: '340px' }}>
            <i className="bi bi-exclamation-triangle" style={{ fontSize: '1.75rem', display: 'block', marginBottom: '0.5rem', color: '#b06000' }}></i>
            <div style={{ fontWeight: '600', color: '#334155', marginBottom: '0.25rem' }}>No image generated</div>
            <div style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
              We were unable to create an image for this post. This can happen with certain content.
            </div>
          </div>
        </div>
      ) : (
        // Dry-run / test-mode placeholder (admin-only, never shown to real users).
        <div style={{
          width: '100%',
          aspectRatio: '1200/630',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '1rem',
          fontWeight: '500'
        }}>
          <div style={{ textAlign: 'center', padding: '1rem' }}>
            <i className="bi bi-image" style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }}></i>
            AI-Generated Image
          </div>
        </div>
      )}

      {!isFailed && (
      <div style={{ padding: '1rem' }}>
        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: altTextEntry ? '0.75rem' : 0 }}>
          <button
            onClick={handleDownload}
            disabled={isDryRun}
            style={{
              ...iconButtonStyle,
              opacity: isDryRun ? 0.5 : 1,
              cursor: isDryRun ? 'not-allowed' : 'pointer'
            }}
            title={isDryRun ? 'Not available in test mode' : 'Download image'}
          >
            <i className="bi bi-download"></i>
          </button>
          <button
            onClick={() => setShowPrompt(!showPrompt)}
            style={iconButtonStyle}
            title={showPrompt ? 'Hide prompt' : 'View prompt'}
          >
            <i className={`bi ${showPrompt ? 'bi-eye-slash' : 'bi-eye'}`}></i>
          </button>
        </div>

        {/* Alt text block (only when alt text exists) */}
        {altTextEntry && (
          <div style={{
            padding: '0.75rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: showPrompt ? '0.75rem' : 0
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
                  Alt Text
                </div>
                <div style={{ fontSize: '0.85rem', lineHeight: '1.4', color: '#1a1a2e' }}>
                  {altTextEntry.alt_text}
                </div>
                <div style={{
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  color: getCounterColor(altTextEntry.char_count, 125),
                  marginTop: '0.25rem'
                }}>
                  {altTextEntry.char_count}/125
                </div>
              </div>
              <button
                onClick={handleCopyAlt}
                style={{
                  ...iconButtonStyle,
                  backgroundColor: copiedAlt ? '#198754' : '#f8f9fa',
                  color: copiedAlt ? '#fff' : '#495057',
                  flexShrink: 0
                }}
                title={copiedAlt ? 'Copied!' : 'Copy alt text'}
              >
                <i className={`bi ${copiedAlt ? 'bi-check' : 'bi-files'}`}></i>
              </button>
            </div>
          </div>
        )}

        {/* Collapsible prompt viewer */}
        {showPrompt && image.prompt_used && (
          <div style={{
            padding: '0.75rem',
            backgroundColor: '#f1f5f9',
            borderRadius: '8px',
            fontSize: '0.8rem',
            lineHeight: '1.5',
            color: '#475569',
            whiteSpace: 'pre-wrap'
          }}>
            <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
              Image Prompt
            </div>
            {image.prompt_used}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default ImageCard;
